import React, { useCallback, useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import StaffShell from "@/components/layout/StaffShell";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, ShieldX, Clock, CheckCircle2, XCircle, RefreshCcw } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { hasPermission } from "@/lib/permissions";
import { ReauthCodeFields } from "@/components/auth/ReauthCodeDialog";

/**
 * Manager Approval queue — shows pending Void / Refund requests raised
 * by Edit-only staff so managers with `location.orders_pos.delete`
 * can Approve or Deny them. Also shows history (approved / denied).
 */
export default function ApprovalsQueue() {
  const { bid } = useParams();
  const { staff } = useAuth();
  const canOpen = hasPermission(staff, "location.orders_pos.delete");
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [canAuthorise, setCanAuthorise] = useState(false);
  const [loading, setLoading] = useState(false);
  const [branch, setBranch] = useState(null);
  const [decision, setDecision] = useState(null); // { approval, action: "approve"|"deny" }
  const [managerEmail, setManagerEmail] = useState("");
  const [pendingId, setPendingId] = useState("");
  const [code, setCode] = useState("");
  const [denyReason, setDenyReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await staffApi.get("/pos-approvals", { params: { status: tab, branch_id: bid, limit: 100 } });
      setRows(r.data.approvals || []);
      setCanAuthorise(!!r.data.can_authorise);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  }, [tab, bid]);

  useEffect(() => {
    (async () => {
      try { setBranch((await staffApi.get(`/branches/${bid}`)).data); } catch (_e) { /* branch header is best-effort */ }
    })();
  }, [bid]);

  useEffect(() => { if (canOpen) load(); }, [load, canOpen]);

  // Page-level gate — sidebar hides this menu, but a bookmarked URL
  // still needs to be blocked. Bounce back to the branch calendar
  // (the standard fallback landing) when the caller lacks the Delete
  // tick under Orders / POS.
  if (!canOpen) {
    return <Navigate to={bid ? `/staff/branch/${bid}/calendar` : "/staff/branches"} replace />;
  }

  const submitDecision = async () => {
    if (!decision) return;
    if (!pendingId || code.trim().length !== 6) {
      toast.error("Enter the 6-digit code emailed to the manager");
      return;
    }
    setBusy(true);
    try {
      const url = decision.action === "approve"
        ? `/pos-approvals/${decision.approval.id}/approve`
        : `/pos-approvals/${decision.approval.id}/deny`;
      await staffApi.post(url, {
        approver_pending_id: pendingId,
        approver_code: code.trim(),
        deny_reason: denyReason,
      });
      toast.success(`Request ${decision.action === "approve" ? "approved" : "denied"}`);
      setDecision(null); setManagerEmail(""); setPendingId(""); setCode(""); setDenyReason("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const kindPill = (k) => {
    const cfg = {
      void:            ["border-rose-300 text-rose-700 bg-rose-50"],
      refund:          ["border-amber-300 text-amber-700 bg-amber-50"],
      line_refund:     ["border-amber-300 text-amber-700 bg-amber-50"],
      price_override:  ["border-sky-300 text-sky-700 bg-sky-50"],
    }[k] || ["border-slate-300 text-slate-700 bg-slate-50"];
    const label = k === "price_override" ? "price override" : (k || "").replace("_", " ");
    return (
      <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${cfg[0]}`}>
        {label}
      </span>
    );
  };
  const statusPill = (s) => {
    const cfg = {
      pending:  ["bg-slate-100 text-slate-700 border-slate-300", <Clock key="i" className="w-3 h-3" />],
      approved: ["bg-emerald-50 text-emerald-700 border-emerald-300", <CheckCircle2 key="i" className="w-3 h-3" />],
      denied:   ["bg-rose-50 text-rose-700 border-rose-300", <XCircle key="i" className="w-3 h-3" />],
      obsolete: ["bg-amber-50 text-amber-800 border-amber-300", <XCircle key="i" className="w-3 h-3" />],
    }[s] || ["bg-muted text-foreground border-border", null];
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${cfg[0]}`}>
        {cfg[1]}{s === "obsolete" ? "no longer needed" : s}
      </span>
    );
  };

  return (
    <StaffShell branchName={branch?.name} context="branch">
      <div className="flex items-center gap-3 mb-4">
        <h1 className="text-xl font-semibold flex items-center gap-2" data-testid="approvals-heading">
          <ShieldCheck className="w-5 h-5" /> Approvals
        </h1>
        <Button size="sm" variant="ghost" onClick={load} data-testid="approvals-refresh">
          <RefreshCcw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      <div className="mb-4 inline-flex rounded-md border border-border overflow-hidden" data-testid="approvals-tabs">
        {["pending", "approved", "denied", "all"].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-sm capitalize ${tab === t ? "bg-primary text-primary-foreground" : "bg-white hover:bg-muted text-foreground"} ${t !== "pending" ? "border-l border-border" : ""}`}
            data-testid={`approvals-tab-${t}`}
          >{t}</button>
        ))}
      </div>

      {!canAuthorise && tab === "pending" && (
        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="approvals-cannot-authorise">
          You&apos;re viewing pending requests, but you don&apos;t hold Order/POS · Delete — a manager needs to sign in on this device to approve or deny.
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => <div key={i} className="animate-pulse h-24 rounded-lg bg-white border border-border" />)}
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-sm text-muted-foreground" data-testid="approvals-empty">
          No {tab === "all" ? "" : tab} requests.
        </div>
      ) : (
        <div className="space-y-2" data-testid="approvals-list">
          {rows.map((a) => (
            <Card key={a.id} data-testid={`approval-row-${a.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {kindPill(a.kind)}
                      {statusPill(a.status)}
                      {a.order_number && <span className="font-mono text-xs text-muted-foreground">#{a.order_number}</span>}
                    </div>
                    <div className="text-sm font-medium truncate">{a.customer_name || "Walk-in"} — {formatBranchMoney(branch, a.order_total || 0)}</div>
                    {a.kind === "price_override" && a.payload && (
                      <div className="mt-1 text-xs text-sky-800 bg-sky-50 border border-sky-200 rounded-md px-2 py-1 inline-flex items-center gap-2" data-testid={`approval-override-detail-${a.id}`}>
                        <span className="font-semibold">Line #{(Number(a.payload.item_index) || 0) + 1}</span>
                        <span>new price <b>{formatBranchMoney(branch, a.payload.new_price)}</b></span>
                        {a.payload.reason ? (
                          <span className="text-sky-700">— {a.payload.reason}</span>
                        ) : null}
                      </div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      Requested by <b>{a.requested_by_name}</b> · {a.created_at?.slice(0, 16).replace("T", " ")}
                      {a.note ? <> · &ldquo;{a.note}&rdquo;</> : null}
                    </div>
                    {a.status === "obsolete" && (
                      <div className="text-xs mt-1 text-amber-800" data-testid={`approval-obsolete-${a.id}`}>
                        Auto-retired{a.obsolete_reason ? ` · ${a.obsolete_reason}` : ""}
                        {a.resolved_at ? ` · ${a.resolved_at.slice(0, 16).replace("T", " ")}` : ""}
                      </div>
                    )}
                    {a.resolved_by_name && a.status !== "obsolete" && (
                      <div className="text-xs mt-1">
                        {a.status === "approved" ? "Approved by " : "Denied by "}
                        <b>{a.resolved_by_name}</b>
                        {a.resolved_at ? ` · ${a.resolved_at.slice(0, 16).replace("T", " ")}` : ""}
                        {a.deny_reason ? <> · &ldquo;{a.deny_reason}&rdquo;</> : null}
                      </div>
                    )}
                  </div>
                  {a.status === "pending" && canAuthorise && (
                    <div className="flex gap-1 shrink-0">
                      <Button size="sm" variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => setDecision({ approval: a, action: "deny" })} data-testid={`approval-deny-${a.id}`}>
                        <ShieldX className="w-4 h-4 mr-1" /> Deny
                      </Button>
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setDecision({ approval: a, action: "approve" })} data-testid={`approval-approve-${a.id}`}>
                        <ShieldCheck className="w-4 h-4 mr-1" /> Approve
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!decision} onOpenChange={(o) => { if (!o) { setDecision(null); setManagerEmail(""); setPendingId(""); setCode(""); setDenyReason(""); } }}>
        <DialogContent className="max-w-md" data-testid="approval-decide-dialog">
          <DialogHeader>
            <DialogTitle>
              {decision?.action === "approve" ? "Approve request" : "Deny request"}
            </DialogTitle>
            <DialogDescription>
              We'll email a one-time code to confirm your identity before you {decision?.action === "approve" ? "authorise" : "reject"} this {decision?.approval?.kind}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <ReauthCodeFields
              purpose="manager_approval"
              askEmail
              email={managerEmail}
              setEmail={setManagerEmail}
              pendingId={pendingId}
              setPendingId={setPendingId}
              code={code}
              setCode={setCode}
              busy={busy}
              setBusy={setBusy}
              idPrefix="approval-decide"
            />
            {decision?.action === "deny" && (
              <div>
                <Label>Reason (optional)</Label>
                <Input value={denyReason} onChange={(e) => setDenyReason(e.target.value)} data-testid="approval-decide-reason" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDecision(null)} disabled={busy} data-testid="approval-decide-cancel">Cancel</Button>
            <Button
              onClick={submitDecision}
              disabled={busy || !pendingId || code.trim().length !== 6}
              className={decision?.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700 text-white"}
              data-testid="approval-decide-confirm"
            >
              {busy ? "Working…" : (decision?.action === "approve" ? "Approve" : "Deny")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </StaffShell>
  );
}
